import { Link } from '@tanstack/react-router'
import { formatBytes, useStreamDailyEvents, useStreams } from '@bloo-team/core-shell'
import type { HomeWidgetProps } from '@bloo-team/core-shell'
import { compactCount, useTopExtractors } from '../lib/extractors'
import { useSources } from '../lib/sources'
import { eventsToday, sourceHealth, streamFlow } from './home-data'

/**
 * Datafabric's home widgets (D7): ingest today, storage volume, inactive
 * sources, stream flow and top extractors. Each renders one `hud-panel` grid
 * item with its own span class — the core HomeGrid lays them out by
 * manifest `order`.
 *
 * Live since home-live-v1: every widget reads a hook the Streams, Storage,
 * Sources or Extractors page already uses (shared query keys, one cache);
 * demo mode comes from those hooks (rule 6). What the old demo bento drew
 * but no endpoint carries — a live eps rate and its trend, hourly stream
 * cells, a silent-source trend — is not faked: it waits on CB-36 / CB-37
 * (docs/home.md §Gaps).
 */

/** Device page the inactive count is taken over — the Streams page's source join uses the same size. */
export const SOURCES_PAGE = 1000

const p = (w: HomeWidgetProps, to: string) => `/${w.clusterId}/${w.scopeId}${to}` as string

/**
 * Row 1 hero: events ingested today across every stream
 * (`daily-stream-details`, a running count). Not a rate — no endpoint
 * carries eps or its trend (CB-36).
 */
export function IngestHero(props: HomeWidgetProps) {
  const dailyQ = useStreamDailyEvents(props)
  const total = eventsToday(dailyQ.data)
  const reporting = dailyQ.data ? Object.values(dailyQ.data).filter((n) => n > 0).length : 0
  return (
    <Link to={p(props, '/fabric/streams')} className="hud-panel hud-span-4" aria-label="Ingest today">
      <div className="hud-stack">
        <div className="hud-row hud-row-between">
          <span className="hud-eyebrow"><b>Datafabric</b> · ingest<span className="app-live-dot" /></span>
          <span className="hud-unit">today so far</span>
        </div>
        {dailyQ.isPending ? <span className="hud-unit" role="status">loading…</span> : null}
        {dailyQ.isError ? <span className="hud-unit" role="status">ingest unavailable</span> : null}
        {total !== null ? (
          <>
            <div className="hud-row" style={{ alignItems: 'baseline', gap: 'var(--bloo-space-2)' }}>
              <span className="hud-value hud-value-lg">{compactCount(total)}</span>
              <span className="hud-unit">events</span>
            </div>
            <span className="hud-unit">{reporting} streams reporting · no eps rate · CB-36</span>
          </>
        ) : null}
      </div>
    </Link>
  )
}

/**
 * Storage volume: used on disk, ABSOLUTE, summed from the same
 * stream-management read the Streams and Storage pages use. The ring it
 * replaced drew a ratio against an invented 100G; it returns only when a
 * quota endpoint exists (CB-32, docs/storage-requirements.md §Home widget).
 */
export function VolumeRing(props: HomeWidgetProps) {
  const { data, isPending, isError } = useStreams(props)
  const used = data ? data.streams.reduce((a, s) => a + s.size_on_disk, 0) : null
  return (
    <Link to={p(props, '/fabric/storage')} className="hud-panel hud-span-2" aria-label="Storage volume">
      <div className="hud-stack st-volume">
        <span className="hud-eyebrow"><b>DF</b> · volume</span>
        {isPending ? <span className="hud-unit" role="status">loading…</span> : null}
        {isError ? <span className="hud-unit" role="status">volume unavailable</span> : null}
        {used !== null ? (
          <>
            <span className="hud-value">{formatBytes(used)}</span>
            <span className="hud-unit">used on disk · {data?.streams.length ?? 0} streams</span>
          </>
        ) : null}
        <span className="hud-unit">no quota · CB-32</span>
      </div>
    </Link>
  )
}

/**
 * Inactive sources over the scope's device list. `active` / `inactive` is
 * all the endpoint says; the count covers the first SOURCES_PAGE devices and
 * says so when the scope is bigger. No trend (CB-37).
 */
export function SourcesCard(props: HomeWidgetProps) {
  const { data, isPending, isError } = useSources({ ...props, pageNo: 1, pageSize: SOURCES_PAGE })
  const health = data ? sourceHealth(data.devices, data.total) : null
  return (
    <Link
      to={p(props, '/fabric/sources')}
      className="hud-panel hud-span-3"
      data-severity={health?.inactive ? 'critical' : undefined}
      aria-label="Inactive sources"
    >
      <div className="hud-stack">
        <span className="hud-eyebrow"><b>DF</b> · sources</span>
        {isPending ? <span className="hud-unit" role="status">loading…</span> : null}
        {isError ? <span className="hud-unit" role="status">sources unavailable</span> : null}
        {health ? (
          <>
            <span className="hud-value">
              {health.inactive}
              <span className="hud-unit"> /{health.total}</span>
            </span>
            <span className="hud-unit">
              inactive{health.partial ? ` · first ${health.loaded} of ${health.total}` : ''}
            </span>
          </>
        ) : null}
      </div>
    </Link>
  )
}

/**
 * Stream flow today: events per stream so far today, silent streams first
 * (red). The old 24-cell hourly strip needs `landingpage/streamheatmap`,
 * never captured and not in the api-client (CB-37).
 */
export function StreamHeat(props: HomeWidgetProps) {
  const streamsQ = useStreams(props)
  const dailyQ = useStreamDailyEvents(props)
  const flow = streamsQ.data && dailyQ.data ? streamFlow(streamsQ.data.streams, dailyQ.data) : null
  const pending = streamsQ.isPending || dailyQ.isPending
  const failed = streamsQ.isError || dailyQ.isError
  return (
    <Link to={p(props, '/fabric/streams')} className="hud-panel hud-span-8" aria-label="Stream flow today">
      <div className="hud-stack">
        <div className="hud-row hud-row-between">
          <span className="hud-eyebrow"><b>Datafabric</b> · stream flow · today</span>
          <span className="hud-unit">{flow ? `${flow.silent} silent · red = no events today` : 'red = no events today'}</span>
        </div>
        {pending ? <span className="hud-unit" role="status">loading…</span> : null}
        {!pending && failed ? <span className="hud-unit" role="status">stream flow unavailable</span> : null}
        {flow && flow.rows.length === 0 ? <span className="hud-unit" role="status">no streams in this scope</span> : null}
        {flow?.rows.length ? (
          <div className="streams">
            {flow.rows.map((r) => (
              <div key={r.stream} className="stream-row">
                <span className="stream-name">{r.stream}</span>
                <span className="stream-bar" data-gap={r.silent || undefined}>
                  <span style={{ width: `${Math.max(r.v, r.events ? 1 : 0)}%` }} />
                </span>
                <span className="stream-count">{compactCount(r.events)}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </Link>
  )
}

/**
 * Top extractors by parsed events over 24h — `POST /api/parsers/top`
 * (captured 2026-09-08; the payload is an object keyed by rank, normalised in
 * the api-client). Live data, unlike its siblings above: the first fabric
 * widget on the api-client. Empty and error states stay honest — no bars
 * are drawn from nothing.
 */
export function TopExtractors(props: HomeWidgetProps) {
  const { data, isPending, isError } = useTopExtractors(props)
  const rows = data ?? []
  const max = rows[0]?.events ?? 0
  return (
    <Link to={p(props, '/fabric/extractors')} className="hud-panel hud-span-4" aria-label="Top extractors · 24h">
      <div className="hud-stack">
        <span className="hud-eyebrow"><b>DF</b> · top extractors · 24h</span>
        {isPending ? <span className="hud-unit" role="status">loading…</span> : null}
        {isError ? <span className="hud-unit" role="status">top extractors unavailable</span> : null}
        {!isPending && !isError && rows.length === 0 ? <span className="hud-unit" role="status">no parsed events in the window</span> : null}
        {rows.length ? (
          <ol className="hud-stack top-ext" style={{ gap: 'var(--bloo-space-1)' }} aria-label="Top extractors">
            {rows.map((t) => (
              <li key={t.extractorId} className="hud-row hud-row-between top-ext-row">
                <span className="mono top-ext-name">{t.name}</span>
                <span className="hud-row" style={{ gap: 'var(--bloo-space-2)', alignItems: 'center' }}>
                  <span className="top-ext-bar" aria-hidden="true">
                    <span style={{ width: max ? `${Math.max(2, Math.round((t.events / max) * 100))}%` : 0 }} />
                  </span>
                  <span className="mono hud-unit top-ext-n">{compactCount(t.events)}</span>
                </span>
              </li>
            ))}
          </ol>
        ) : null}
      </div>
    </Link>
  )
}
